import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, Lock, Trash2, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

function complianceScore(md) {
  if (!md) return 0;
  let s = 0;
  if (md.is_device_admin) s += 25;
  if (md.security?.encryption_status === 'encrypted') s += 25;
  if (md.security?.screen_lock_enabled) s += 25;
  if (md.security_patch_level) {
    const days = (Date.now() - new Date(md.security_patch_level).getTime()) / 86400000;
    if (days < 90) s += 25;
  }
  return s;
}

function ScoreBadge({ score }) {
  const color = score >= 75 ? 'var(--green)' : score >= 50 ? 'var(--amber)' : 'var(--red)';
  const bg    = score >= 75 ? 'var(--green-dim)' : score >= 50 ? 'var(--amber-dim)' : 'var(--red-dim)';
  return (
    <span style={{ color, fontWeight: 700, fontSize: 12, minWidth: 38, display: 'inline-block', textAlign: 'center' }}>
      {score}
    </span>
  );
}

function StatusBadge({ status }) {
  if (status === 'executed') return <span style={{ color: 'var(--green)', fontSize: 12 }}><CheckCircle size={12} style={{ marginRight: 3 }} />Ejecutado</span>;
  if (status === 'failed')   return <span style={{ color: 'var(--red)', fontSize: 12 }}><XCircle size={12} style={{ marginRight: 3 }} />Fallido</span>;
  return <span style={{ color: 'var(--amber)', fontSize: 12 }}><Clock size={12} style={{ marginRight: 3 }} />Pendiente</span>;
}

export default function MDM() {
  const [devices, setDevices]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [history, setHistory]     = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [modal, setModal]         = useState(null); // { command, label, confirmText }
  const [confirmInput, setConfirmInput] = useState('');
  const [sending, setSending]     = useState(false);
  const [result, setResult]       = useState(null);

  const token   = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/assets?limit=500`, { headers });
      const data = await r.json();
      const all = Array.isArray(data.assets) ? data.assets : Array.isArray(data) ? data : [];
      setDevices(all.filter(a => a.agent_type === 'android'));
    } catch { setDevices([]); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  const loadHistory = useCallback(async (hostname) => {
    setLoadingHist(true);
    try {
      const r = await fetch(`${API_URL}/android-agent/devices/${hostname}/commands`, { headers });
      const data = await r.json();
      setHistory(Array.isArray(data.commands) ? data.commands : []);
    } catch { setHistory([]); }
    setLoadingHist(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectDevice = (dev) => {
    setSelected(dev);
    setResult(null);
    setModal(null);
    loadHistory(dev.hostname);
  };

  const sendCommand = async () => {
    if (!modal || !selected) return;
    setSending(true);
    setResult(null);
    try {
      const r = await fetch(`${API_URL}/android-agent/devices/${selected.hostname}/command`, {
        method: 'POST', headers,
        body: JSON.stringify({ command: modal.command }),
      });
      const data = await r.json();
      setResult({ ok: r.ok, message: data.message || data.error });
      if (r.ok) loadHistory(selected.hostname);
    } catch (e) {
      setResult({ ok: false, message: e.message });
    }
    setSending(false);
    setModal(null);
    setConfirmInput('');
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const cmdLabel = { lock: 'Bloquear', wipe: 'Borrar datos', wipe_full: 'Wipe total' };

  return (
    <div className="page-content">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Smartphone size={22} style={{ color: 'var(--blue)' }} />
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>MDM — Gestión de Móviles</h1>
        <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={loadDevices}>
          <RefreshCw size={13} style={{ marginRight: 4 }} />Actualizar
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 20 }}>

        {/* Lista de dispositivos */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 14 }}>
            Dispositivos Android ({devices.length})
          </div>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-2)' }}>Cargando...</div>
          ) : !devices.length ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-2)' }}>Sin dispositivos Android registrados.</div>
          ) : (
            <div style={{ overflowY: 'auto', maxHeight: 600 }}>
              {devices.map(dev => {
                const md    = dev.agent_metadata || {};
                const score = complianceScore(md);
                const isSelected = selected?.id === dev.id;
                return (
                  <div key={dev.id}
                    onClick={() => selectDevice(dev)}
                    style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer', background: isSelected ? 'var(--blue-light)' : 'transparent', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Smartphone size={18} style={{ color: isSelected ? 'var(--blue)' : 'var(--ink-2)', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: isSelected ? 'var(--blue)' : '#111' }}>{dev.hostname}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-2)', marginTop: 2 }}>
                        {md.manufacturer} {md.model} · Android {md.android_version}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 1 }}>
                        Último reporte: {fmt(dev.last_seen)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <ScoreBadge score={score} />
                      <span style={{ fontSize: 10, color: 'var(--ink-3)' }}>compliance</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Panel de comandos */}
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <Smartphone size={18} style={{ color: 'var(--blue)' }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{selected.hostname}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-2)' }}>
                    {(selected.agent_metadata || {}).manufacturer} {(selected.agent_metadata || {}).model}
                  </div>
                </div>
                <Link to={`/assets/${selected.id}`} className="btn btn-sm" style={{ marginLeft: 'auto', fontSize: 11 }}>
                  Ver detalle
                </Link>
              </div>

              {result && (
                <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 6, background: result.ok ? 'var(--green-dim)' : 'var(--red-dim)', color: result.ok ? 'var(--green)' : 'var(--red)', fontSize: 13 }}>
                  {result.ok ? '✓' : '✗'} {result.message}
                </div>
              )}

              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Comandos remotos</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button className="btn" style={{ justifyContent: 'flex-start', gap: 10, background: '#1e40af', color: 'var(--surface)', border: 'none' }}
                  onClick={() => { setModal({ command: 'lock', label: 'Bloquear dispositivo', confirmText: null }); setResult(null); }}>
                  <Lock size={15} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>Bloquear pantalla</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>Bloquea el dispositivo de forma remota inmediatamente</div>
                  </div>
                </button>
                <button className="btn" style={{ justifyContent: 'flex-start', gap: 10, background: 'var(--red)', color: 'var(--surface)', border: 'none' }}
                  onClick={() => { setModal({ command: 'wipe', label: 'Borrar datos corporativos', confirmText: 'CONFIRMAR' }); setResult(null); }}>
                  <Trash2 size={15} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>Borrar datos</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>Elimina datos corporativos del dispositivo</div>
                  </div>
                </button>
                <button className="btn" style={{ justifyContent: 'flex-start', gap: 10, background: 'var(--red)', color: 'var(--surface)', border: 'none' }}
                  onClick={() => { setModal({ command: 'wipe_full', label: 'Wipe total del dispositivo', confirmText: 'BORRAR TODO' }); setResult(null); }}>
                  <Trash2 size={15} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>Wipe total</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>Borra todo el dispositivo incluyendo almacenamiento externo</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Historial de comandos */}
            <div className="card" style={{ padding: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>Historial de comandos</div>
              {loadingHist ? (
                <div style={{ color: 'var(--ink-2)', fontSize: 13 }}>Cargando...</div>
              ) : !history.length ? (
                <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Sin comandos enviados.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {history.map(cmd => (
                    <div key={cmd.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'var(--bg-subtle)', borderRadius: 6, border: '1px solid var(--border)' }}>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: 12 }}>{cmdLabel[cmd.command] || cmd.command}</span>
                        {cmd.requested_by && <span style={{ color: 'var(--ink-3)', fontSize: 11, marginLeft: 8 }}>por {cmd.requested_by}</span>}
                        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>{fmt(cmd.created_at)}</div>
                        {cmd.error_msg && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 2 }}>{cmd.error_msg}</div>}
                      </div>
                      <StatusBadge status={cmd.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmación */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 28, maxWidth: 400, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{modal.label}</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 16 }}>
              Dispositivo: <strong>{selected?.hostname}</strong>
              <br />Este comando se ejecutará en el próximo ciclo del agente.
              {modal.command === 'wipe' && <><br /><br /><span style={{ color: 'var(--red)' }}>⚠ Acción irreversible — se eliminarán los datos corporativos.</span></>}
              {modal.command === 'wipe_full' && <><br /><br /><span style={{ color: 'var(--red)', fontWeight: 700 }}>☢ ADVERTENCIA: se borrarán TODOS los datos del dispositivo.</span></>}
            </div>
            {modal.confirmText && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 6 }}>
                  Escribí <strong>{modal.confirmText}</strong> para confirmar:
                </div>
                <input className="form-control" value={confirmInput}
                  onChange={e => setConfirmInput(e.target.value)}
                  placeholder={modal.confirmText} style={{ fontFamily: 'monospace' }} />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-sm" onClick={() => { setModal(null); setConfirmInput(''); }}>Cancelar</button>
              <button className="btn btn-sm" style={{ background: 'var(--red)', color: 'var(--surface)', border: 'none' }}
                disabled={sending || (modal.confirmText && confirmInput !== modal.confirmText)}
                onClick={sendCommand}>
                {sending ? 'Enviando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
